import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';

interface VideoPlayerProps {
  src: string;
  poster?: string | null;
  className?: string;
  /** Context for [VideoPlaybackDebug] logs. */
  debug?: {
    reviewId?: string | null;
    mediaId?: string | null;
    mediaType?: string | null;
  };
  autoPlay?: boolean;
  controls?: boolean;
}

/** Best-effort container guess from the URL path (after we've moved to honest extensions). */
const guessTypeFromUrl = (url: string): string | undefined => {
  const lower = url.split('?')[0].toLowerCase();
  if (lower.endsWith('.mp4') || lower.endsWith('.m4v')) return 'video/mp4';
  if (lower.endsWith('.mov')) return 'video/quicktime';
  if (lower.endsWith('.webm')) return 'video/webm';
  return undefined;
};

/** Detect Capacitor / iOS WKWebView so logs make the platform obvious. */
const getPlatformLabel = (): string => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  const capacitor = typeof (window as any)?.Capacitor !== 'undefined';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (capacitor && isIOS) return 'capacitor-ios-wkwebview';
  if (capacitor && isAndroid) return 'capacitor-android-webview';
  if (isIOS) return 'mobile-safari';
  if (isAndroid) return 'mobile-android';
  return 'desktop-web';
};

/** Returns one of '', 'maybe', 'probably' — '' means the browser refuses to even try. */
const canPlay = (type: string | undefined): string => {
  if (!type || typeof document === 'undefined') return '';
  try {
    const v = document.createElement('video');
    return v.canPlayType(type) || '';
  } catch {
    return '';
  }
};

/**
 * Hardened HTML5 video player for review media: pre-gates on canPlayType,
 * logs container/MIME/platform context for debugging, and shows a friendly
 * fallback when playback fails instead of a black, dead player.
 */
const VideoPlayer = ({
  src,
  poster,
  className = '',
  debug,
  autoPlay = true,
  controls = true,
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [resolvedMime, setResolvedMime] = useState<string | undefined>(undefined);
  const guessedType = guessTypeFromUrl(src);
  const platform = useMemo(getPlatformLabel, []);

  // Reset state when the underlying source changes — prevents stale failure
  // states when navigating between videos in a carousel.
  useEffect(() => {
    setFailed(false);
    setResolvedMime(undefined);
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
        v.removeAttribute('src');
        v.load();
      } catch {
        /* ignore */
      }
    }
  }, [src]);

  // Probe the actual Content-Type once for logging — this is the only way
  // to see if storage is serving (e.g.) video/quicktime to a browser that
  // can't decode it.
  useEffect(() => {
    let cancelled = false;
    fetch(src, { method: 'HEAD' })
      .then((r) => {
        if (cancelled) return;
        const ct = r.headers.get('content-type') || undefined;
        const len = r.headers.get('content-length') || undefined;
        const range = r.headers.get('accept-ranges') || undefined;
        setResolvedMime(ct);
        const decisions = {
          asGuessed: canPlay(guessedType),
          asResolved: canPlay(ct),
          asMp4: canPlay('video/mp4'),
          asWebm: canPlay('video/webm'),
          asQt: canPlay('video/quicktime'),
        };
        console.info('[VideoPlaybackDebug] HEAD', {
          ...debug,
          src,
          platform,
          httpStatus: r.status,
          contentType: ct,
          contentLength: len,
          acceptRanges: range,
          guessedType,
          canPlayType: decisions,
        });
        // If every candidate type is rejected, fail fast with the friendly UI
        // rather than waiting for the <video> element to render a black frame.
        if (!decisions.asGuessed && !decisions.asResolved && !decisions.asMp4 && !decisions.asWebm && !decisions.asQt) {
          console.warn('[VideoPlaybackDebug] canPlayType rejected all candidates — surfacing fallback UI', {
            ...debug, src, platform, contentType: ct,
          });
          setFailed(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[VideoPlaybackDebug] HEAD failed', { ...debug, src, platform, error: String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [src, debug, guessedType, platform]);

  if (failed) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 rounded-md bg-black/60 text-white/85 px-6 py-10 text-center ${className}`}
        role="alert"
      >
        <AlertCircle className="h-6 w-6 text-white/60" aria-hidden />
        <div>
          <p className="text-sm font-medium">This video could not be loaded</p>
          <p className="mt-1 text-[12px] text-white/55">
            The clip may use a format this device can’t play.
          </p>
        </div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      poster={poster || undefined}
      controls={controls}
      autoPlay={autoPlay}
      playsInline
      preload="metadata"
      className={className}
      onLoadedMetadata={() =>
        console.info('[VideoPlaybackDebug] loadedmetadata', {
          ...debug,
          src,
          platform,
          contentType: resolvedMime,
          guessedType,
          duration: videoRef.current?.duration,
          videoWidth: videoRef.current?.videoWidth,
          videoHeight: videoRef.current?.videoHeight,
        })
      }
      onPlay={() =>
        console.info('[VideoPlaybackDebug] play', { ...debug, src, platform })
      }
      onStalled={() =>
        console.warn('[VideoPlaybackDebug] stalled', { ...debug, src, platform })
      }
      onError={(e) => {
        const el = e.currentTarget;
        const err = el.error;
        console.error('[VideoPlaybackDebug] error', {
          ...debug,
          src,
          platform,
          contentType: resolvedMime,
          guessedType,
          code: err?.code,
          message: err?.message,
          networkState: el.networkState,
          readyState: el.readyState,
        });
        setFailed(true);
      }}
    >
      {/* Try the URL-derived type first; then mp4 (covers legacy MOV stored
          with a .webm filename); then WebM; then untyped as a last resort. */}
      {guessedType ? <source src={src} type={guessedType} /> : null}
      <source src={src} type="video/mp4" />
      <source src={src} type="video/webm" />
      <source src={src} />
    </video>
  );
};

export default VideoPlayer;
